import Link from 'next/link'
import styled, { keyframes } from 'styled-components'
import sendTrackingEvent from 'mParticle/sendTrackingEvent'
import { colors, fonts, media } from 'shared/lib'
import { Raffle } from 'shared/types/raffles'
import { RaffleCountdown } from './RaffleCountdown'
import { formatRafflePrice, getRaffleDisplayStatus } from './raffleUtils'
import { t } from 'localization'

const TP = 'shared.components.Raffles.RaffleCard'

interface IRaffleCardProps {
  raffle: Raffle
}

export const RaffleCard = ({ raffle }: IRaffleCardProps) => {
  const {
    slug,
    brand,
    productName,
    mainImage,
    rafflePrice,
    currency,
    startDate,
    endDate,
    badgeTitle,
    badgeSubtitle,
  } = raffle

  const displayStatus = getRaffleDisplayStatus(raffle)
  const isFeatured = displayStatus === 'active' || displayStatus === 'upcoming'

  const handleCardClick = () => {
    sendTrackingEvent('DRAWING_CARD_TAP', {
      product_template_slug: slug,
      product_name: productName,
      drawing_status: displayStatus,
    })
  }

  return (
    <Link href={`/drawings/${slug}`} passHref legacyBehavior>
      <CardLink data-qa="raffle-card" $featured={isFeatured} onClick={handleCardClick}>
        <ImageContainer>
          {badgeTitle && (
            <CardBadge data-qa="raffle-badge">
              <CardBadgeTitle>{badgeTitle}</CardBadgeTitle>
              {badgeSubtitle && <CardBadgeSubtitle>{badgeSubtitle}</CardBadgeSubtitle>}
            </CardBadge>
          )}
          <ImageWrapper $featured={isFeatured}>
            <ProductImage src={mainImage.src} alt={productName} />
          </ImageWrapper>
        </ImageContainer>
        <CardInfo>
          <TextBlock>
            <Brand>{brand}</Brand>
            <Name>{productName}</Name>
            {rafflePrice > 0 && (
              <Price>{formatRafflePrice(rafflePrice, currency)}</Price>
            )}
          </TextBlock>
          <StatusRow>
            {displayStatus === 'active' && (
              <>
                <ActiveIndicator>
                  <ActiveDot />
                  <ActiveLabel>{t(`${TP}.open`, 'Open')}</ActiveLabel>
                </ActiveIndicator>
                <CountdownWrapper>
                  {t(`${TP}.closesIn`, 'Closes in')} <RaffleCountdown targetDate={endDate} />
                </CountdownWrapper>
              </>
            )}
            {displayStatus === 'upcoming' && (
              <>
                <UpcomingLabel>{t(`${TP}.comingSoon`, 'Coming Soon')}</UpcomingLabel>
                <CountdownWrapper>
                  {t(`${TP}.opensIn`, 'Opens in')} <RaffleCountdown targetDate={startDate} />
                </CountdownWrapper>
              </>
            )}
            {displayStatus === 'closed' && (
              <ClosedDate>{t(`${TP}.closed`, 'Closed')}</ClosedDate>
            )}
          </StatusRow>
        </CardInfo>
      </CardLink>
    </Link>
  )
}

const blink = keyframes`
  0%, 60% { opacity: 1; }
  60.01%, 100% { opacity: 0; }
`

const CardLink = styled.a<{ $featured: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 12px;
  text-decoration: none;
  color: ${colors.FC2_BLACK};
  cursor: pointer;
  padding: 0 12px;

  ${({ $featured }) =>
    $featured &&
    `
    grid-column: span 2;
  `}

  ${media.larger`
    padding: 0 24px;
    grid-column: span 1;
  `}
`

const ImageWrapper = styled.div<{ $featured: boolean }>`
  width: 100%;
  aspect-ratio: ${({ $featured }) => ($featured ? 'initial' : '3 / 2')};
  display: flex;
  align-items: flex-end;
  justify-content: center;
  overflow: visible;
`

const ProductImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: 50% 100%;
  transform: scale(1.12);
  transform-origin: bottom center;
`

const CardInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  z-index: 4;

  ${media.larger`
    padding-left: 3.2%;
  `}
`

const TextBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const Brand = styled.div`
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 1.2;
  color: ${colors.FC2_BLACK};
`

const Name = styled.div`
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 1.3;
  height: 2lh;
  text-transform: uppercase;
  color: ${colors.FC2_BLACK};
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`

const Price = styled.div`
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 1.3;
  color: ${colors.FC2_BLACK};
`

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const ActiveIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const ActiveDot = styled.span`
  width: 6px;
  height: 6px;
  background-color: ${colors.FC2_RED};
  flex-shrink: 0;
  animation: ${blink} 1s steps(1, end) infinite;
`

const ActiveLabel = styled.span`
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 1.2;
  text-transform: uppercase;
  color: ${colors.FC2_RED};
  white-space: nowrap;
`

const UpcomingLabel = styled.span`
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 1;
  text-transform: uppercase;
  color: ${colors.FC2_BLACK};
`

const CountdownWrapper = styled.span`
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 1;
  color: ${colors.FC2_BLACK};

  span {
    font-weight: 500;
  }
`

const ClosedDate = styled.span`
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 1.3;
  color: ${colors.FC2_LIGHT_GREY};
`

const ImageContainer = styled.div`
  position: relative;
  padding-top: 30px;

  ${media.larger`
    padding-top: 0;
  `}
`

const CardBadge = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  mix-blend-mode: difference;
`

const CardBadgeTitle = styled.div`
  ${fonts.SUB_TEXT}
  font-weight: 500;
  line-height: 1.2;
  text-transform: uppercase;
  color: ${colors.FC2_WHITE};
`

const CardBadgeSubtitle = styled.div`
  ${fonts.SUB_TEXT}
  line-height: 1.2;
  color: ${colors.FC2_WHITE};
`
